import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  Select,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";

import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { exportDebtorsToPDF } from "../../utils/exportDebtors";

export default function DebtorTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const queryString = useMemo(() => {
    const params = ["page_size=all"];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    if (selectedCustomer) params.push(`customer=${selectedCustomer}`);
    return params.join("&");
  }, [startDate, endDate, selectedBranch, selectedCustomer]);

  const {
    data: debtorsData,
    isLoading,
    isError,
  } = useFetchObjects(
    ["customer-debtors-report", queryString],
    `shop/customer-debtors/?${queryString}`,
    token
  );

  const { data: customers } = useFetchObjects(
    ["customers", "debtors-report"],
    "shop/customers?page_size=all",
    token
  );

  const debtors = useMemo(
    () => debtorsData?.results || debtorsData || [],
    [debtorsData]
  );
  const customersList = useMemo(
    () => customers?.results || customers || [],
    [customers]
  );
  const customerOptions = useMemo(
    () => [{ id: "", name: t("reports.all_customers") }, ...customersList],
    [customersList, t]
  );
  const selectedCustomerOption = useMemo(
    () =>
      customerOptions.find(
        (customer) => String(customer.id) === String(selectedCustomer)
      ) || customerOptions[0],
    [customerOptions, selectedCustomer]
  );

  const filteredDebtors = useMemo(() => {
    return debtors.filter((row) => {
      const loanDate = row.loan_date ? dayjs(row.loan_date) : null;

      if (
        selectedCustomer &&
        String(row.customer_details?.id ?? row.customer) !==
          String(selectedCustomer)
      ) {
        return false;
      }

      if (startDate && loanDate && loanDate.isBefore(startDate.startOf("day"))) {
        return false;
      }

      if (endDate && loanDate && loanDate.isAfter(endDate.endOf("day"))) {
        return false;
      }

      if ((startDate || endDate) && !loanDate) {
        return false;
      }

      return true;
    });
  }, [debtors, selectedCustomer, startDate, endDate]);

  const totalCount = filteredDebtors.length;

  const displayRows = useMemo(() => {
    if (rowsPerPage === -1) return filteredDebtors;
    const startIndex = (page - 1) * rowsPerPage;
    return filteredDebtors.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredDebtors, page, rowsPerPage]);

  const totalAmount = useMemo(
    () =>
      filteredDebtors.reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0),
    [filteredDebtors]
  );
  const totalSettled = useMemo(
    () =>
      filteredDebtors.reduce(
        (acc, p) => acc + (parseFloat(p.settled_amount) || 0),
        0
      ),
    [filteredDebtors]
  );
  const totalOutstanding = useMemo(
    () =>
      filteredDebtors.reduce((acc, p) => {
        const outstanding =
          p.outstanding_amount != null
            ? parseFloat(p.outstanding_amount)
            : (parseFloat(p.amount) || 0) - (parseFloat(p.settled_amount) || 0);
        return acc + (outstanding || 0);
      }, 0),
    [filteredDebtors]
  );

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = () => {
    const today = new Date().toISOString().slice(0, 10);
    exportDebtorsToPDF(filteredDebtors, `debtors_${today}.pdf`);
    handleClose();
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          {t("reports.error_fetching_data")}{" "}
          <RefreshIcon sx={{ cursor: "pointer", verticalAlign: "middle" }} />
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <DatePicker
                  label={t("reports.datepicker_start")}
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    setPage(1);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t("reports.datepicker_end")}
                  value={endDate}
                  onChange={(value) => {
                    setEndDate(value);
                    setPage(1);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <Autocomplete
                  fullWidth
                  options={customerOptions}
                  value={selectedCustomerOption}
                  onChange={(event, newValue) => {
                    setSelectedCustomer(
                      newValue?.id ? String(newValue.id) : ""
                    );
                    setPage(1);
                  }}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("debtor.customer", "Customer")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedCustomer("");
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("reports.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-debtors-button"
                    aria-controls={open ? "export-debtors-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export", "Export")}
                  </Button>
                  <Menu
                    id="export-debtors-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{ "aria-labelledby": "export-debtors-button" }}
                  >
                    <MenuItem onClick={handleExportClick}>
                      {t("ReportIndex.export_pdf", "Export PDF")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <Paper sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t("debtor.summary", "Debtors Summary")}
        </Typography>
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <Typography variant="body2">
            {t("debtor.amount", "Amount")}: <strong>{totalAmount.toFixed(2)}</strong>
          </Typography>
          <Typography variant="body2">
            {t("debtor.settled", "Settled")}: <strong>{totalSettled.toFixed(2)}</strong>
          </Typography>
          <Typography variant="body2">
            {t("debtor.outstanding", "Outstanding")}:{" "}
            <strong>{totalOutstanding.toFixed(2)}</strong>
          </Typography>
        </Box>
      </Paper>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6">
          {t("debtor.reportTitle", "Debtors Report")} ({totalCount})
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={-1}>{t("reports.all", "All")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>#</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.customer", "Customer")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.account", "Account")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.amount", "Amount")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.settled", "Settled")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.outstanding", "Outstanding")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.currency", "Currency")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.exchangeRate", "Rate")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.convertedAmount", "Converted")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.loanDate", "Date")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("debtor.notes", "Notes")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {t("debtor.noRecords", "No debtor records found.")}
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map((row, index) => {
                const rowIndex =
                  rowsPerPage === -1
                    ? index + 1
                    : (page - 1) * rowsPerPage + index + 1;
                const outstanding =
                  row.outstanding_amount != null
                    ? row.outstanding_amount
                    : (parseFloat(row.amount) || 0) -
                      (parseFloat(row.settled_amount) || 0);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>{rowIndex}</TableCell>
                    <TableCell>{row.customer_details?.name || "—"}</TableCell>
                    <TableCell>{row.account_details?.name || "—"}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.settled_amount ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={Number(outstanding) > 0 ? "warning" : "success"}
                        label={Number(outstanding).toFixed(2)}
                      />
                    </TableCell>
                    <TableCell>{row.currency_details?.title || "—"}</TableCell>
                    <TableCell>{row.exchange_rate ?? "—"}</TableCell>
                    <TableCell>{row.converted_amount ?? "—"}</TableCell>
                    <TableCell>
                      {row.loan_date
                        ? new Date(row.loan_date).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell>{row.notes || "—"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rowsPerPage !== -1 && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage) || 1}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}
    </>
  );
}
